import { unwrapResult } from '@reduxjs/toolkit';
import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from "../hooks";
import { AppDispatch } from "../store";
import {
    getProductsAction,
    checkoutAction,
    submitWithoutPaymentAction,
    addToCartAction,
    removeFromCartAction,
    updateCartItemAction,
    updateCartItemLearnerAction,
    clearCartAction,
    clearAllAction,
    clearErrorsAction,
    checkProformaStatusAction,
    clearProformaStatusAction,
    setPendingProformaAction,
} from './actions';
import {
    productsSelector,
    isFetchingSelector,
    hasFetchedSelector,
    errorMessageSelector,
    cartSelector,
    cartTotalSelector,
    cartCountSelector,
    paymentUrlSelector,
    proformaSelector,
    proformaStatusSelector,
} from './selectors';
import { CartItem } from '../../models/shop.model';

export const useShopStore = () => {
    const dispatch: AppDispatch = useAppDispatch();

    const products = useAppSelector(productsSelector);
    const isFetching = useAppSelector(isFetchingSelector);
    const hasFetched = useAppSelector(hasFetchedSelector);
    const errorMessage = useAppSelector(errorMessageSelector);
    const cart = useAppSelector(cartSelector);
    const cartTotal = useAppSelector(cartTotalSelector);
    const cartCount = useAppSelector(cartCountSelector);
    const paymentUrl = useAppSelector(paymentUrlSelector);
    const proforma = useAppSelector(proformaSelector);
    const proformaStatus = useAppSelector(proformaStatusSelector);

    const getProducts = useCallback(
        (customerId: number) => {
            return dispatch(getProductsAction({ customerId })).then(unwrapResult);
        },
        [dispatch]
    );

    const checkProformaStatus = useCallback(
        (arg: { customerId: number, proformaId: string }) => {
            return dispatch(checkProformaStatusAction(arg)).then(unwrapResult);
        },
        [dispatch]
    );

    const checkout = useCallback(
        (arg: { customerId: number, cart: CartItem[] }) => {
            return dispatch(checkoutAction(arg)).then(unwrapResult);
        },
        [dispatch]
    );

    const submitWithoutPayment = useCallback(
        (arg: { customerId: number, cart: CartItem[] }) => {
            return dispatch(submitWithoutPaymentAction(arg)).then(unwrapResult);
        },
        [dispatch]
    );

    const addToCart = useCallback(
        (item: CartItem) => {
            return dispatch(addToCartAction(item));
        },
        [dispatch]
    );

    const removeFromCart = useCallback(
        (id: string) => {
            return dispatch(removeFromCartAction(id));
        },
        [dispatch]
    );

    const updateCartItem = useCallback(
        (id: string, quantity: number) => {
            return dispatch(updateCartItemAction({ id, quantity }));
        },
        [dispatch]
    );

    const updateCartItemLearner = useCallback(
        (id: string, learnerId: number | null) => {
            return dispatch(updateCartItemLearnerAction({ id, learnerId }));
        },
        [dispatch]
    );

    const clearCart = useCallback(
        () => {
            return dispatch(clearCartAction());
        },
        [dispatch]
    );

    const clearProformaStatus = useCallback(
        () => {
            return dispatch(clearProformaStatusAction());
        },
        [dispatch]
    );

    const setPendingProforma = useCallback(
        (arg: { paymentUrl?: string; proforma?: any }) => {
            return dispatch(setPendingProformaAction(arg));
        },
        [dispatch]
    );

    const clearAll = useCallback(
        () => {
            return dispatch(clearAllAction()).then(unwrapResult);
        },
        [dispatch]
    );

    const clearErrors = useCallback(
        () => {
            return dispatch(clearErrorsAction()).then(unwrapResult);
        },
        [dispatch]
    );

    return {
        products,
        isFetching,
        hasFetched,
        errorMessage,
        cart,
        cartTotal,
        cartCount,
        paymentUrl,
        proforma,
        proformaStatus,
        getProducts,
        checkProformaStatus,
        checkout,
        submitWithoutPayment,
        addToCart,
        removeFromCart,
        updateCartItem,
        updateCartItemLearner,
        clearCart,
        clearProformaStatus,
        setPendingProforma,
        clearAll,
        clearErrors,
    } as const;
};
